import { Constructor } from '@angular/cdk/table';
import { FormGroup } from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DigitalPublicationDataService } from 'src/app/content-management/shared/services/digital-publication-data.service';
import { DialogService } from '../services/dialog.service';
import {
  AutoCompleteData,
  IAutoCompleteData,
} from 'src/app/shared-form-sections/interfaces/i-auto-complete-data';

type WithProductFormSectionType = {
  isSearchingProducts: boolean;
  productsSearchResult: IAutoCompleteData[];
  productExactMatch: IAutoCompleteData;
  _initProduct(product: string): void;
  _initProductValueChanges(getPreviousProduct: () => string | null): void;
  _buildProduct(): string;
  onAddNewProduct(text: string);
};

type ProductFormSectionRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: { PRODUCT: () => string };
  destroy$: Subject<boolean>;
  digitalPublicationDataService: DigitalPublicationDataService;
  _dialogService: DialogService;
};

export function WithProductFormSection<
  TBase extends Constructor<ProductFormSectionRequirementsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithProductFormSectionType;
  prototype: WithProductFormSectionType;
} & TBase {
  return class WithProductFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    isSearchingProducts: boolean = false;
    productsSearchResult: IAutoCompleteData[];
    productExactMatch: IAutoCompleteData;

    _initProduct(product: string): void {
      if (product) {
        this.form
          .get(this.FORM_PATHS.PRODUCT())
          .setValue(new AutoCompleteData(product, product));
      }

      this.form
        .get(this.FORM_PATHS.PRODUCT())
        .valueChanges.pipe(
          distinctUntilChanged(),
          debounceTime(500),
          tap(() => {
            this.isSearchingProducts = true;
          }),
          switchMap((searchedValue: string) => {
            const getDataObs$ =
              searchedValue && typeof searchedValue === 'string'
                ? this.digitalPublicationDataService.searchProducts$(
                    searchedValue
                  )
                : of(null);

            return getDataObs$.pipe(
              finalize(() => {
                this.isSearchingProducts = false;
              })
            );
          })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              this.productsSearchResult = response.results.map(
                (p) => new AutoCompleteData(p.text, p.text)
              );
              if (response.exactMatch) {
                this.productExactMatch = new AutoCompleteData(
                  response.exactMatch.text,
                  response.exactMatch.text
                );
              } else {
                this.productExactMatch = null;
              }
            } else {
              this.productsSearchResult = null;
              this.productExactMatch = null;
            }
          },
        });
    }

    _initProductValueChanges(getPreviousProduct: () => string | null): void {
      this.form
        .get(this.FORM_PATHS.PRODUCT())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue.displayName === getPreviousProduct()) {
              this.form.get(this.FORM_PATHS.PRODUCT()).markAsPristine();
            }
          },
        });
    }

    _buildProduct(): string {
      return this.form.get(this.FORM_PATHS.PRODUCT()).value.displayName;
    }

    onAddNewProduct(text: string) {
      return this.digitalPublicationDataService.createProduct$(text).subscribe({
        next: (_) =>
          this.form
            .get(this.FORM_PATHS.PRODUCT())
            .setValue(new AutoCompleteData(text, text)),
      });
    }
  };
}
